import React, { useEffect, useState, useRef } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Box,
    Typography,
    Chip,
    InputAdornment,
    IconButton,
    Tooltip,
} from '@mui/material';
import Api from "../../Services/Api";
import PropTypes from 'prop-types';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import SortIcon from '@mui/icons-material/Sort';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ShuffleIcon from '@mui/icons-material/Shuffle';

const EditKeywordsDialog = ({
    open,
    onClose,
    feed,
    onKeywordsUpdated,
    setSnackbarMessage,
    setSnackbarSeverity,
    setSnackbarOpen,
    keywordLists
}) => {
    const [keywordsInput, setKeywordsInput] = useState('');
    const [nameInput, setNameInput] = useState('');
    const [selectedKeywordsSet, setSelectedKeywordsSet] = useState('');
    const [showNewKeywordsForm, setShowNewKeywordsForm] = useState(true);
    const [fetchedKeywordLists, setFetchedKeywordLists] = useState([]);
    const [keywordSearch, setKeywordSearch] = useState('');
    const [sortMode, setSortMode] = useState('none'); // 'none', 'asc', 'desc'
    const [randomSelected, setRandomSelected] = useState(false);
    const keywordSelectRef = useRef(null);
    const [selectOpen, setSelectOpen] = useState(false);

    const effectiveKeywordLists = fetchedKeywordLists.length > 0 ? fetchedKeywordLists : keywordLists;

    // Toggle sort mode: none -> desc -> asc -> none
    const toggleSortMode = () => {
        if (sortMode === 'none') setSortMode('desc');
        else if (sortMode === 'desc') setSortMode('asc');
        else setSortMode('none');
    };

    // Select a random list from top 20 RPM lists
    const selectRandomTopList = (event) => {
        // Prevent the dropdown from reopening when clicking the button
        event.stopPropagation();
        
        // Sort the lists by RPM in descending order
        const sortedLists = [...(effectiveKeywordLists || [])].sort((a, b) => {
            const rpmA = a.stats?.last7days?.rpm || 0;
            const rpmB = b.stats?.last7days?.rpm || 0;
            return rpmB - rpmA;
        });
        
        // Get the top 20 lists (or whatever is available if less than 20)
        const topLists = sortedLists.slice(0, Math.min(20, sortedLists.length));
        
        if (topLists.length === 0) {
            setSnackbarMessage('No keyword lists available to choose from');
            setSnackbarSeverity('warning');
            setSnackbarOpen(true);
            return;
        }
        
        // Select a random list from the top lists
        const randomIndex = Math.floor(Math.random() * topLists.length);
        const randomList = topLists[randomIndex];
        
        setSelectedKeywordsSet(randomList.id);
        setShowNewKeywordsForm(false); // Make sure we're in selection mode, not creation mode
        setRandomSelected(true);
        
        // Show a success message
        setSnackbarMessage(`Randomly selected "${randomList.name}" from top ${topLists.length} RPM lists`);
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
        
        // Close the dropdown
        setSelectOpen(false);
        
        // Clear random flag after a timeout
        setTimeout(() => setRandomSelected(false), 3000);
    };

    // Filter keyword lists based on search and sort
    const filteredKeywordLists = (effectiveKeywordLists || [])
        .filter(list => 
            list.name.toLowerCase().includes(keywordSearch.toLowerCase())
        )
        .sort((a, b) => {
            if (sortMode === 'none') return 0;
            
            const rpmA = a.stats?.last7days?.rpm || 0;
            const rpmB = b.stats?.last7days?.rpm || 0;
            
            return sortMode === 'desc' ? rpmB - rpmA : rpmA - rpmB;
        });

    const formatKeywords = (keywords) => {
        if (!keywords) return '';
        return Array.isArray(keywords) ? keywords.join(', ') : String(keywords);
    };

    useEffect(() => {
        if (feed) {
            setNameInput(`Keywords for Feed ${feed.supply_feed_id || feed.id}`);
            
            if (feed.keyword_list_id) {
                setSelectedKeywordsSet(feed.keyword_list_id);
                const selectedList = effectiveKeywordLists.find(list => list.id === feed.keyword_list_id);
                setKeywordsInput(selectedList && selectedList.keywords ? formatKeywords(selectedList.keywords) : '');
            } 
            else if (feed.keywords) {
                setKeywordsInput(formatKeywords(feed.keywords));
                setSelectedKeywordsSet('');
            } else {
                setKeywordsInput('');
                setSelectedKeywordsSet('');
            }
            
            setShowNewKeywordsForm(!feed.keyword_list_id);
        }
    }, [feed, effectiveKeywordLists]);

    useEffect(() => {
        if (!showNewKeywordsForm && selectedKeywordsSet) {
            const selectedList = effectiveKeywordLists.find(list => list.id === selectedKeywordsSet);
            setKeywordsInput(selectedList && selectedList.keywords ? formatKeywords(selectedList.keywords) : '');
        }
    }, [selectedKeywordsSet, showNewKeywordsForm, effectiveKeywordLists]);

    useEffect(() => {
        fetchKeywordLists();
    }, []);

    const fetchKeywordLists = async () => {
        try {
            const response = await Api.getKeywordLists(true);
            const lists = (response && response.data) ? response.data : response;
            setFetchedKeywordLists(lists || []);
        } catch (error) {
            console.error('Error fetching keyword lists:', error);
            setFetchedKeywordLists([]);
        }
    };

    const handleSave = async () => {
        try {
            if (showNewKeywordsForm) {
                const keywordArray = keywordsInput
                    .split(/[,\n|]+/)
                    .map(keyword => keyword.trim())
                    .filter(keyword => keyword.length > 0)
                    .map(keyword => keyword.replace(/[^a-zA-Z0-9\s]/g, ''));

                const createResponse = await Api.createKeywordList({
                    name: nameInput,
                    keywords: keywordArray,
                    supply_feed_id: feed.supply_feed_id
                });

                if (createResponse && createResponse.data) {
                    setSnackbarMessage(`Keywords updated successfully for feed ${feed.supply_feed_id}`);
                    setSnackbarSeverity('success');
                    setSnackbarOpen(true);
                    onKeywordsUpdated(feed.supply_feed_id, keywordArray);
                    onClose();
                }
            } else {
                if (!selectedKeywordsSet) {
                    setSnackbarMessage('Please select a keywords list');
                    setSnackbarSeverity('error');
                    setSnackbarOpen(true);
                    return;
                }

                const selectedList = effectiveKeywordLists.find(list => list.id === selectedKeywordsSet);
                
                if (!selectedList) {
                    setSnackbarMessage('Selected keywords list not found');
                    setSnackbarSeverity('error');
                    setSnackbarOpen(true);
                    return;
                }

                await Api.updateSpinSupplyFeedKeywords(feed.id, {
                    keyword_list_id: selectedKeywordsSet,
                    supply_feed_id: feed.supply_feed_id
                });

                setSnackbarMessage(`Keywords updated successfully for feed ${feed.supply_feed_id}`);
                setSnackbarSeverity('success');
                setSnackbarOpen(true);
                onKeywordsUpdated(feed.supply_feed_id, selectedList.keywords);
                onClose();
            }
        } catch (error) {
            console.error("Failed to update keywords:", error);
            
            if (error && error.data) {
                const { message, errors } = error.data;
                if (errors) {
                    const errorMessage = Object.values(errors)
                        .flat()
                        .join('. ');
                    setSnackbarMessage(errorMessage);
                } else {
                    setSnackbarMessage(message || 'Failed to update keywords. Please try again.');
                }
            } else {
                setSnackbarMessage('Failed to update keywords. Please try again.');
            }
            
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Edit Keywords</DialogTitle>
            <DialogContent>
                {!showNewKeywordsForm ? (
                    <>
                        <FormControl fullWidth sx={{ mt: 2 }}>
                            <InputLabel>Select Keywords List</InputLabel>
                            <Select
                                ref={keywordSelectRef}
                                value={selectedKeywordsSet}
                                onChange={(e) => setSelectedKeywordsSet(e.target.value)}
                                displayEmpty
                                label="Select Keywords List"
                                open={selectOpen}
                                onOpen={() => setSelectOpen(true)}
                                onClose={() => setSelectOpen(false)}
                                MenuProps={{
                                    PaperProps: {
                                        style: {
                                            maxHeight: 400,
                                        },
                                    },
                                    MenuListProps: {
                                        subheader: (
                                            <Box sx={{ p: 1, position: 'sticky', top: 0, bgcolor: 'background.paper', zIndex: 1 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                                    <TextField
                                                        placeholder="Search keyword lists..."
                                                        value={keywordSearch}
                                                        onChange={(e) => setKeywordSearch(e.target.value)}
                                                        fullWidth
                                                        size="small"
                                                        InputProps={{
                                                            startAdornment: (
                                                                <InputAdornment position="start">
                                                                    <SearchIcon />
                                                                </InputAdornment>
                                                            ),
                                                        }}
                                                        variant="outlined"
                                                        onClick={(e) => e.stopPropagation()}
                                                        onKeyDown={(e) => e.stopPropagation()}
                                                    />
                                                    <Tooltip title={sortMode === 'none' 
                                                        ? "Sort by RPM" 
                                                        : sortMode === 'desc' 
                                                            ? "Sort by highest RPM" 
                                                            : "Sort by lowest RPM"}>
                                                        <IconButton 
                                                            size="small" 
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                toggleSortMode();
                                                            }}
                                                            sx={{ ml: 1 }}
                                                        >
                                                            {sortMode === 'none' && <SortIcon />}
                                                            {sortMode === 'desc' && <ArrowDownwardIcon />}
                                                            {sortMode === 'asc' && <ArrowUpwardIcon />}
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Pick random from top 20 RPM lists">
                                                        <IconButton 
                                                            size="small" 
                                                            onClick={selectRandomTopList}
                                                            sx={{ ml: 0.5 }}
                                                            color={randomSelected ? "primary" : "default"}
                                                        >
                                                            <ShuffleIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Box>
                                                {sortMode !== 'none' && (
                                                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                                        <Chip 
                                                            size="small" 
                                                            label={`Sorted by ${sortMode === 'desc' ? 'highest' : 'lowest'} RPM`} 
                                                            onDelete={() => setSortMode('none')}
                                                            variant="outlined"
                                                        />
                                                    </Box>
                                                )}
                                            </Box>
                                        ),
                                    },
                                }}
                                renderValue={(selected) => {
                                    if (!selected) {
                                        return <em>None</em>;
                                    }
                                    
                                    const selectedItem = effectiveKeywordLists.find(item => item.id === selected);
                                    return selectedItem ? selectedItem.name : <em>None</em>;
                                }}
                            >
                                <MenuItem value="">
                                    <em>None</em>
                                </MenuItem>
                                {filteredKeywordLists.map((set) => (
                                    <MenuItem key={set.id} value={set.id}>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                                            <Typography variant="body1">{set.name}</Typography>
                                            <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                                                <Chip 
                                                    size="small" 
                                                    label={`${set.keywords?.length || 0} keywords`} 
                                                    variant="outlined" 
                                                />
                                                <Chip 
                                                    size="small" 
                                                    label={`${set.stats?.last7days?.searches?.toLocaleString() || 0} searches`} 
                                                    variant="outlined" 
                                                />
                                                <Chip 
                                                    size="small" 
                                                    label={`$${set.stats?.last7days?.rpm?.toFixed(2) || '0.00'} RPM`} 
                                                    variant="outlined" 
                                                />
                                            </Box>
                                        </Box>
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <Button
                            startIcon={<AddIcon />}
                            onClick={() => setShowNewKeywordsForm(true)}
                            sx={{ mt: 2 }}
                        >
                            Create New Keywords List
                        </Button>
                    </>
                ) : (
                    <>
                        <TextField
                            label="Name"
                            value={nameInput}
                            onChange={(e) => setNameInput(e.target.value)}
                            fullWidth
                            variant="outlined"
                            margin="normal"
                        />
                        <TextField
                            label="Keywords"
                            value={keywordsInput}
                            onChange={(e) => setKeywordsInput(e.target.value)}
                            fullWidth
                            multiline
                            rows={4}
                            helperText="Enter keywords separated by commas, new lines, or | symbols."
                            variant="outlined"
                            sx={{ mt: 2 }}
                        />
                        <Button
                            onClick={() => setShowNewKeywordsForm(false)}
                            sx={{ mt: 2 }}
                        >
                            Select Existing Keywords List
                        </Button>
                    </>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color="secondary">
                    Cancel
                </Button>
                <Button 
                    onClick={handleSave} 
                    variant="contained" 
                    color="primary"
                    disabled={showNewKeywordsForm ? 
                        (!nameInput.trim() || !keywordsInput.trim()) : 
                        !selectedKeywordsSet
                    }
                >
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    );
};

EditKeywordsDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    feed: PropTypes.object,
    onKeywordsUpdated: PropTypes.func.isRequired,
    setSnackbarMessage: PropTypes.func,
    setSnackbarSeverity: PropTypes.func,
    setSnackbarOpen: PropTypes.func,
    keywordLists: PropTypes.array.isRequired,
};

// Default no-op functions if the parent doesn't supply these props.
EditKeywordsDialog.defaultProps = {
    setSnackbarMessage: () => {},
    setSnackbarSeverity: () => {},
    setSnackbarOpen: () => {},
};

export default EditKeywordsDialog; 